import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BonsaiLogo } from "@/components/BonsaiLogo";
import {
  LayoutDashboard, FilePlus2, Package, Users, LogOut, User2, Menu, X, Megaphone, ScrollText, CalendarClock, FileSignature,
} from "lucide-react";

const linkBase =
  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const salesNav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/configuratore", label: "Nuova Offerta", icon: FilePlus2 },
    { to: "/listino", label: "Listino", icon: ScrollText },
    ...(user?.contracts_enabled ? [{ to: "/scadenze", label: "Scadenze", icon: CalendarClock }] : []),
    { to: "/profilo", label: "Profilo Agenzia", icon: User2 },
  ];
  const adminNav = [
    { to: "/", label: "Offerte", icon: LayoutDashboard, end: true },
    { to: "/admin/catalogo", label: "Catalogo & Prezzi", icon: Package },
    { to: "/listino", label: "Listino", icon: ScrollText },
    { to: "/scadenze", label: "Scadenze", icon: CalendarClock },
    { to: "/admin/contratto", label: "Modello Contratto", icon: FileSignature },
    { to: "/admin/commerciali", label: "Commerciali", icon: Users },
    { to: "/admin/comunicazioni", label: "Comunicazioni", icon: Megaphone },
  ];
  const nav = isAdmin ? adminNav : salesNav;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = () => (
    <>
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setOpen(false)}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `${linkBase} ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-slate-600 hover:bg-secondary hover:text-navy"
            }`
          }
        >
          <n.icon size={18} />
          {n.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex bg-secondary">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-border fixed h-screen">
        <div className="px-6 h-16 flex items-center border-b border-border">
          <BonsaiLogo size={26} />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <p className="px-4 pb-2 text-[11px] uppercase tracking-widest text-muted-foreground font-bold">
            {isAdmin ? "Gestionale" : "Configuratore"}
          </p>
          <NavItems />
        </nav>
        <div className="p-4 border-t border-border">
          <div className="px-2 pb-3">
            <p className="text-sm font-bold text-navy truncate">{user?.name || user?.email}</p>
            <p className="text-xs text-muted-foreground truncate">
              {isAdmin ? "Amministratore" : user?.agency_name || "Commerciale"}
            </p>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className={`${linkBase} w-full text-slate-600 hover:bg-destructive/10 hover:text-destructive`}
          >
            <LogOut size={18} /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-16 bg-white border-b border-border z-40 flex items-center justify-between px-4">
        <BonsaiLogo size={24} />
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-navy">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed top-16 inset-x-0 bg-white border-b border-border z-40 p-4 space-y-1">
          <NavItems />
          <button
            onClick={doLogout}
            className={`${linkBase} w-full text-destructive`}
          >
            <LogOut size={18} /> Esci
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">{children}</main>
    </div>
  );
}
